import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import i18n from '../../i18n'
import { invalidateQueries, updateToast } from '../helpers'
import { createUpdateAgencyOffice, getAgency, getAgencyOfficeById, getAgencyOffices, updateAgency } from './functions'
import { Agency, AgencyOffice, AgencyOfficeListResponse, CreateUpdateAgencyOfficePayload } from './types'

const agency = {
    GetAgency: {
        useQuery: (options?: Omit<UseQueryOptions<Agency, AxiosError>, 'queryKey' | 'queryFn'>) => {
            return useQuery({
                queryKey: ['agency'],
                queryFn: getAgency,
                staleTime: 1 * 60 * 1000,
                ...options,
            })
        },
    },
    UpdateAgency: {
        useMutation: (options?: UseMutationOptions<Agency, AxiosError, Record<string, unknown>>) => {
            return useMutation({
                onMutate: () => toast.loading(i18n.t('toast.loading') as string),
                mutationFn: (data: Record<string, unknown>) => updateAgency(data),
                onSuccess: (data, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.success') as string, 'success')

                    invalidateQueries(['agency'])
                    invalidateQueries(['currentAgent'])
                },
                onError: (error, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.error') as string, 'error')
                },
                ...options,
            })
        },
    },
    GetAgencyOffices: {
        useQuery: (options?: Omit<UseQueryOptions<AgencyOfficeListResponse, AxiosError>, 'queryKey' | 'queryFn'>) => {
            return useQuery({
                queryKey: ['agency', 'offices'],
                queryFn: getAgencyOffices,
                staleTime: 1 * 60 * 1000,
                ...options,
            })
        },
    },
    GetAgencyOffice: {
        useQuery: (id: string | number, options?: Omit<UseQueryOptions<AgencyOffice, AxiosError>, 'queryKey' | 'queryFn'>) => {
            return useQuery({
                queryKey: ['agency', 'offices', id],
                queryFn: () => getAgencyOfficeById(id),
                staleTime: 1 * 60 * 1000,
                enabled: Boolean(id),
                ...options,
            })
        },
    },
    CreateUpdateAgencyOffice: {
        useMutation: (options?: UseMutationOptions<AgencyOffice, AxiosError, CreateUpdateAgencyOfficePayload>) => {
            return useMutation({
                onMutate: () => toast.loading(i18n.t('toast.loading') as string),
                mutationFn: (payload: CreateUpdateAgencyOfficePayload) => createUpdateAgencyOffice(payload),
                onSuccess: (data, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.success') as string, 'success')

                    invalidateQueries(['agency', 'offices'])
                    variables.id && invalidateQueries(['agency', 'offices', variables.id.toString()])
                    invalidateQueries(['currentAgent'])
                },
                onError: (error, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.error') as string, 'error')
                },
                ...options,
            })
        },
    },
}

export default agency
